import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ArrowLeftIcon,
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const MemberForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    birthDate: '',
    gender: 'M',
    emergencyContact: '',
    emergencyPhone: '',
    notes: '',
    photo: null,
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch member data if editing
  const { data: memberData, isLoading } = useQuery(
    ['member', id],
    async () => {
      const response = await axios.get(`/members/${id}`);
      return response.data;
    },
    {
      enabled: isEditing,
      onSuccess: (data) => {
        setFormData({
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          birthDate: (data.dateOfBirth || data.birthDate) ? new Date(data.dateOfBirth || data.birthDate).toISOString().split('T')[0] : '',
          gender: data.gender ? (data.gender === 'male' ? 'M' : data.gender === 'female' ? 'F' : data.gender) : 'M',
          emergencyContact: data.emergencyContactName || '',
          emergencyPhone: data.emergencyContactPhone || '',
          notes: data.notes || '',
          photo: null,
        });
        if (data.photo) {
          setPhotoPreview(data.photo);
        }
      },
    }
  );

  // Create member mutation
  const createMemberMutation = useMutation(
    (memberData) => axios.post('/members', memberData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    {
      onSuccess: () => {
        toast.success('Membre créé avec succès!');
        queryClient.invalidateQueries('members');
        navigate('/members');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Erreur lors de la création';
        toast.error(message);
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      },
    }
  );

  // Update member mutation
  const updateMemberMutation = useMutation(
    (memberData) => axios.put(`/members/${id}`, memberData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
    {
      onSuccess: () => {
        toast.success('Membre mis à jour avec succès!');
        queryClient.invalidateQueries('members');
        queryClient.invalidateQueries(['member', id]);
        navigate('/members');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
        toast.error(message);
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('La photo ne peut pas dépasser 5MB');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        photo: file,
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation côté client : date de naissance ne doit pas être future
    if (formData.birthDate && new Date(formData.birthDate) > new Date()) {
      toast.error('La date de naissance ne peut pas être dans le futur');
      return;
    }

    const formDataToSend = new FormData();
    
    // Append all form fields => mapping vers le backend
    const [firstName, ...lastParts] = (formData.name || '').trim().split(' ');
    const lastName = lastParts.join(' ');

    formDataToSend.append('firstName', firstName || '');
    formDataToSend.append('lastName', lastName || '');
    if (formData.email) formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    if (formData.address) formDataToSend.append('address', formData.address);
    if (formData.birthDate) formDataToSend.append('dateOfBirth', formData.birthDate);
    formDataToSend.append('gender', formData.gender);
    if (formData.emergencyContact) formDataToSend.append('emergencyContactName', formData.emergencyContact);
    if (formData.emergencyPhone) formDataToSend.append('emergencyContactPhone', formData.emergencyPhone);
    if (formData.notes) formDataToSend.append('notes', formData.notes);
    if (formData.photo) formDataToSend.append('photo', formData.photo);

    // Ne pas inclure l'ancien système d'itération automatique

    if (isEditing) {
      updateMemberMutation.mutate(formDataToSend);
    } else {
      createMemberMutation.mutate(formDataToSend);
    }
  };

  const isSubmitting = createMemberMutation.isLoading || updateMemberMutation.isLoading;

  if (isEditing && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white/30"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/members')}
            className="p-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditing ? 'Modifier le Membre' : 'Nouveau Membre'}
            </h1>
            <p className="text-white/60 mt-2">
              {isEditing ? 'Mettre à jour les informations du membre' : 'Ajouter un nouveau membre à la salle'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Photo Upload */}
          <div className="lg:col-span-1">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-fit">
              <h3 className="text-lg font-semibold text-white mb-4">Photo du Membre</h3>
              
              <div className="flex flex-col items-center space-y-4">
                <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center overflow-hidden">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="h-16 w-16 text-white" />
                  )}
                </div>
                
                <div className="w-full">
                  <label className="block w-full">
                    <span className="sr-only">Choisir une photo</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600 file:transition-colors"
                    />
                  </label>
                  <p className="text-xs text-white/50 mt-2">
                    PNG, JPG jusqu'à 5MB
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6">
              <h3 className="text-lg font-semibold text-white">Informations Personnelles</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Full Name */}
                <div className="md:col-span-2">
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Nom Complet *
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 border ${
                        errors.name ? 'border-red-500' : 'border-white/20'
                      } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Ahmed Ben Ali"
                      required
                    />
                  </div>
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-white/10 border ${
                      errors.email ? 'border-red-500' : 'border-white/20'
                    } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="ahmed@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Téléphone *
                  </label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 border ${
                        errors.phone ? 'border-red-500' : 'border-white/20'
                      } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="+213 XXX XXX XXX"
                      required
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
                  )}
                </div>

                {/* Birth Date */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Date de Naissance
                  </label>
                  <div className="relative">
                    <CalendarDaysIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="date"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleChange}
                      max={new Date().toISOString().split('T')[0]}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 border ${
                        errors.birthDate ? 'border-red-500' : 'border-white/20'
                      } rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                  </div>
                  {errors.birthDate && (
                    <p className="mt-1 text-sm text-red-400">{errors.birthDate}</p>
                  )}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Adresse
                  </label>
                  <div className="relative">
                    <MapPinIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 border ${
                        errors.address ? 'border-red-500' : 'border-white/20'
                      } rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Alger, Algérie"
                    />
                  </div>
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-400">{errors.address}</p>
                  )}
                </div>

                {/* Emergency Contact */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Contact d'Urgence
                  </label>
                  <input
                    type="text"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nom du contact"
                  />
                </div>

                {/* Emergency Phone */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Téléphone d'Urgence
                  </label>
                  <input
                    type="tel"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+213 XXX XXX XXX"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Notes sur le membre..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/members')}
            className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {isEditing ? 'Mise à jour...' : 'Création...'}
              </div>
            ) : (
              isEditing ? 'Mettre à jour' : 'Créer le membre'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MemberForm; 